import { Keyboard, KeyboardResize, KeyboardStyle } from '@capacitor/keyboard';
import { isPlatform } from '@ionic/core';
import { useCallback, useEffect, useState } from 'react';
import { KeyboardService } from '../service/keyboard.service';
import { useDarkMode } from './dark-mode.hook';

const setKeyboardResizeMode = async (
  mode?: KeyboardResize
): Promise<() => Promise<void>> => {
  if (!isPlatform('ios')) {
    return async () => {};
  }

  const currentMode: { mode: KeyboardResize } = await Keyboard.getResizeMode();
  await Keyboard.setResizeMode({
    mode: mode ?? IOS_KEYBOARD_RESIZE_MODE,
  });

  const revertResideMode = async () => {
    await Keyboard.setResizeMode(currentMode);
  };

  return revertResideMode;
};

const useThemedKeyboard = (): void => {
  const isDarkMode = useDarkMode();
  useEffect(() => {
    setKeyboard(isDarkMode);
  }, [isDarkMode]);
};

// if we don't use resize body then we can see issues with the bottom
// panel jumping. because we don't use resize native it actually
// causes an issue with our side panel height not shrinking on ios when the
// keyboard is presented so we apply custom logic in our side panel
// to account for it
export const IOS_KEYBOARD_RESIZE_MODE = KeyboardResize.Body;

const setKeyboard = async (isDarkMode: boolean): Promise<void> => {
  try {
    await Keyboard.setStyle({
      style: isDarkMode ? KeyboardStyle.Dark : KeyboardStyle.Light,
    });

    await setKeyboardResizeMode();
  } catch {
    // do nothing if platform does not support Keyboard
  }
};

export const getHighestElement = <
  T extends Element & { style?: CSSStyleDeclaration },
>(
  selector: string
): T | null | undefined => {
  const els = document.querySelectorAll<T>(selector);

  let elToReturn: T | undefined;
  let zIndex = -1;

  for (let i = 0; i < els?.length; i++) {
    const el = els[i];

    const newZIndex = Number(el.style?.getPropertyValue('z-index') ?? -1);

    if (newZIndex > zIndex) {
      elToReturn = el;
      zIndex = newZIndex;
    }
  }

  return elToReturn;
};

interface KeyboardEvent extends Event {
  keyboardHeight?: number;
}

const useKeyboardWillShow = (
  cb: (event: KeyboardEvent & { isPhysicalKeyboard?: boolean }) => void
): void => {
  const callback = useCallback(
    async (e: KeyboardEvent) => {
      const isPhysicalKeyboard = await KeyboardService.isPhysicalKeyboard();
      cb({
        ...e,
        isPhysicalKeyboard,
      });
    },
    [cb]
  );

  useEffect(() => {
    window.addEventListener('keyboardWillShow', callback);
    return () => {
      window.removeEventListener('keyboardWillShow', callback);
    };
  }, [callback]);
};

const useKeyboardWillHide = (cb: () => void): void => {
  const callback = useCallback(() => {
    cb();
  }, [cb]);

  useEffect(() => {
    window.addEventListener('keyboardWillHide', callback);
    return () => {
      window.removeEventListener('keyboardWillHide', callback);
    };
  }, [callback]);
};

const useKeyboardDidHide = (cb: () => void): void => {
  const callback = useCallback(() => {
    cb();
  }, [cb]);

  useEffect(() => {
    window.addEventListener('keyboardDidHide', callback);
    return () => {
      window.removeEventListener('keyboardDidHide', callback);
    };
  }, [callback]);
};

const useKeyboardDidShow = (
  cb: (event: KeyboardEvent & { isPhysicalKeyboard?: boolean }) => void
): void => {
  const callback = useCallback(
    async (e: KeyboardEvent) => {
      const isPhysicalKeyboard = await KeyboardService.isPhysicalKeyboard();
      cb({
        ...e,
        isPhysicalKeyboard,
      });
    },
    [cb]
  );

  useEffect(() => {
    window.addEventListener('keyboardDidShow', callback);
    return () => {
      window.removeEventListener('keyboardDidShow', callback);
    };
  }, [callback]);
};

const useKeyboardIsShowing = (): boolean => {
  const [isShowing, setIsShowing] = useState<boolean>(false);

  useKeyboardWillShow(() => setIsShowing(true));
  useKeyboardDidHide(() => setIsShowing(false));

  return isShowing;
};

export {
  useThemedKeyboard,
  useKeyboardDidHide,
  useKeyboardDidShow,
  useKeyboardWillHide,
  useKeyboardWillShow,
  useKeyboardIsShowing,
  setKeyboardResizeMode,
};
